import posthog from 'posthog-js';

/**
 * Initialize PostHog analytics
 * This should be called once when the app loads
 */
export const initPostHog = () => {
  // Support both VITE_POSTHOG_KEY and VITE_PUBLIC_POSTHOG_KEY
  const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY || import.meta.env.VITE_POSTHOG_KEY;
  // Support both VITE_POSTHOG_HOST and VITE_PUBLIC_POSTHOG_HOST
  const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST || import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com';

  // Only initialize if API key is provided
  if (!posthogKey) {
    console.warn('[PostHog] PostHog key not found. PostHog analytics disabled.');
    console.warn('[PostHog] Checked: VITE_PUBLIC_POSTHOG_KEY and VITE_POSTHOG_KEY');
    return null;
  }

  console.log('[PostHog] Initializing with host:', posthogHost);

  // Initialize PostHog
  posthog.init(posthogKey, {
    api_host: posthogHost,
    // Session replay configuration
    session_recording: {
      maskAllInputs: false, // Set to true if you want to mask all input fields for privacy
      maskAllText: false, // Set to true if you want to mask all text for privacy
      recordCrossOriginIframes: false,
    },
    // Capture pageviews automatically
    capture_pageview: true,
    // Capture pageleaves automatically
    capture_pageleave: true,
    // Enable autocapture for clicks, form submissions, etc.
    autocapture: true,
    // Load PostHog asynchronously
    loaded: (posthog) => {
      console.log('[PostHog] Initialized successfully');
      
      // Set up global error handlers after PostHog is loaded
      setupErrorHandlers();
    },
    // Error handling
    _capture_metrics: true,
  });

  return posthog;
};

/**
 * Set up global error handlers for PostHog
 */
const setupErrorHandlers = () => {
  // Handle unhandled JavaScript errors
  window.addEventListener('error', (event) => {
    const posthog = getPostHog();
    if (posthog) {
      posthog.capture('$exception', {
        $exception_message: event.message,
        $exception_type: event.error?.name || 'Error',
        $exception_source: event.filename,
        $exception_line_number: event.lineno,
        $exception_column_number: event.colno,
        $exception_stack_trace: event.error?.stack,
        error_category: 'UNHANDLED_ERROR',
        url: window.location.href,
        user_agent: navigator.userAgent,
      });
    }
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const posthog = getPostHog();
    if (posthog) {
      const error = event.reason;
      posthog.capture('$exception', {
        $exception_message: error?.message || String(error),
        $exception_type: error?.name || 'UnhandledRejection',
        $exception_stack_trace: error?.stack,
        error_category: 'UNHANDLED_PROMISE_REJECTION',
        url: window.location.href,
        user_agent: navigator.userAgent,
      });
    }
  });
};

/**
 * Get PostHog instance
 * Returns null if PostHog is not initialized
 */
export const getPostHog = () => {
  if (typeof window !== 'undefined' && window.posthog) {
    return window.posthog;
  }
  return null;
};

/**
 * Identify a user in PostHog
 * Call this after user authentication
 */
export const identifyUser = (userId, userProperties = {}) => {
  const posthog = getPostHog();
  if (posthog && userId) {
    posthog.identify(userId, userProperties);
    if (import.meta.env.DEV) {
      console.log('[PostHog] User identified:', userId, userProperties);
    }
  }
};

/**
 * Reset user identification (on logout)
 */
export const resetUser = () => {
  const posthog = getPostHog();
  if (posthog) {
    posthog.reset();
    if (import.meta.env.DEV) {
      console.log('[PostHog] User reset');
    }
  }
};

/**
 * Capture a custom event
 * Wrapper around posthog.capture with error handling
 */
export const captureEvent = (eventName, properties = {}) => {
  const posthog = getPostHog();
  if (posthog) {
    try {
      posthog.capture(eventName, properties);
      if (import.meta.env.DEV) {
        console.log('[PostHog] Event captured:', eventName, properties);
      }
    } catch (error) {
      console.error('[PostHog] Error capturing event:', error);
    }
  }
};

/**
 * Capture an error/exception event
 * Use this for handled errors that you want to track
 */
export const captureError = (error, context = {}) => {
  const posthog = getPostHog();
  if (posthog) {
    try {
      posthog.capture('$exception', {
        $exception_message: error?.message || String(error),
        $exception_type: error?.name || 'Error',
        $exception_stack_trace: error?.stack,
        error_category: context.error_category || 'HANDLED_ERROR',
        ...context,
        url: window.location.href,
        timestamp: new Date().toISOString(),
      });
      if (import.meta.env.DEV) {
        console.log('[PostHog] Error captured:', error, context);
      }
    } catch (err) {
      console.error('[PostHog] Error capturing error event:', err);
    }
  }
};

/**
 * Update user properties
 * Use this to update user properties without re-identifying
 */
export const updateUserProperties = (properties) => {
  const posthog = getPostHog();
  if (posthog) {
    try {
      posthog.people.set(properties);
      if (import.meta.env.DEV) {
        console.log('[PostHog] User properties updated:', properties);
      }
    } catch (error) {
      console.error('[PostHog] Error updating user properties:', error);
    }
  }
};

/**
 * Track page view with additional context
 */
export const trackPageView = (pageName, properties = {}) => {
  const posthog = getPostHog();
  if (posthog) {
    try {
      posthog.capture('$pageview', {
        page_name: pageName,
        page_path: window.location.pathname,
        page_url: window.location.href,
        ...properties,
      });
      if (import.meta.env.DEV) {
        console.log('[PostHog] Page view tracked:', pageName, properties);
      }
    } catch (error) {
      console.error('[PostHog] Error tracking page view:', error);
    }
  }
};

// Export posthog instance for direct access if needed
export default posthog;

